import React, { useEffect } from "react";
import { Printer, X as IconX } from "@phosphor-icons/react";
import Logo from "../components/Logo";

const TPO_PERIODS = [
  ["A", "09:15 – 09:45"],
  ["B", "09:45 – 10:15"],
  ["C", "10:15 – 10:45"],
  ["D", "10:45 – 11:15"],
  ["E", "11:15 – 11:45"],
  ["F", "11:45 – 12:15"],
  ["G", "12:15 – 12:45"],
  ["H", "12:45 – 13:15"],
  ["I", "13:15 – 13:45"],
  ["J", "13:45 – 14:15"],
  ["K", "14:15 – 14:45"],
  ["L", "14:45 – 15:15"],
  ["M", "15:15 – 15:30"],
];

const TERMS = [
  {
    term: "POC · Point of Control",
    desc: "The price level where the most trading volume / time happened during the session. The market's gravity well. Price tends to revisit POC.",
  },
  {
    term: "VAH · Value Area High",
    desc: "Upper boundary of the 'value area' — the price range where ~70 % of the session's volume traded. Acceptance above VAH = bullish initiative.",
  },
  {
    term: "VAL · Value Area Low",
    desc: "Lower boundary of the value area (~70 % volume range). Acceptance below VAL = bearish initiative. Rejection at VAL often gives long entries.",
  },
  {
    term: "IB · Initial Balance",
    desc: "The high-low range of the first hour (A + B periods, 09:15–10:15). Sets the day's reference rails. Breakouts of IB define day type.",
  },
  {
    term: "TPO · Time-Price Opportunity",
    desc: "One letter on the Market Profile represents one 30-minute bracket. Each letter at a price level = that bracket touched that price.",
  },
  {
    term: "Delta / CVD",
    desc: "Delta = buy market orders − sell market orders. CVD is its cumulative sum. Tracks who's aggressive — buyers or sellers.",
  },
  {
    term: "Absorption",
    desc: "When aggressive orders hit resting liquidity and price doesn't move. Signals a strong defender at that level.",
  },
];

const RULES = [
  "Be respectful and civil to all members and mentors.",
  "No spam, promotions, or irrelevant content in the group.",
  "Do not share Zoom session recordings outside the group.",
  "During live: use chat or raise hand for questions.",
  "No trading calls outside the context of the session.",
  "Violations may result in removal from the group — no refund.",
];

const LEVELS_GLOSSARY = [
  ["R1 / R2 / R3", "Resistance levels above POC"],
  ["S1 / S2 / S3", "Support levels below POC"],
  ["BULLISH level", "Bias turns positive above this price"],
  ["BEARISH level", "Bias turns negative below this price"],
  ["POC", "Most traded level — key reference point"],
  ["Inner targets", "Shared ONLY inside the WhatsApp group"],
];

const RISK = [
  "Trading involves substantial risk of loss.",
  "Levels and analysis are for educational purposes only.",
  "Always use proper position sizing and stop-losses.",
  "Never risk more than you can afford to lose.",
  "Great Eagle Tech does not provide SEBI-registered investment advice.",
];

const LIVE_FLOW = [
  {
    step: "After Payment",
    body: "Pay ₹999 via Razorpay. The WhatsApp group link is revealed instantly after payment confirmation. Join the group — your Zoom session link will be shared there.",
  },
  {
    step: "Charting Platform",
    body: "Create a brand-new email ID for the free 1-month GoCharting trial. Do NOT use an existing email — new users only. Link will be shared in the group.",
  },
  {
    step: "Before First Live",
    body: "Read the MP terminology section below (POC, VAH, VAL, IB). Install Zoom. Check the group for session timings. Keep a trading journal ready.",
  },
  {
    step: "Opening Auction",
    body: "09:15–09:30 IST — A-period read. Did we open inside / above / below previous-day value? This sets the day-type bias.",
  },
  {
    step: "Initial Balance",
    body: "09:15–10:15 IST — A + B periods together form the IB. Lock IB high and IB low. These are the day's first rails.",
  },
  {
    step: "Intraday",
    body: "10:15 onwards — supply / demand zones, MP rotations, Order Flow imbalances and live trade plans called out.",
  },
  {
    step: "Closing",
    body: "15:15–15:30 IST — M period (15 min). Closing auction read. Where did value end vs where it opened? Sets context for tomorrow.",
  },
];

export default function CommunityHandbook() {
  useEffect(() => {
    document.title = "Great Eagle Tech — Community Handbook";
  }, []);

  return (
    <div data-testid="handbook-root" className="bg-white text-[#0A0A0A] min-h-screen">
      {/* Top action bar — hidden when printing */}
      <div className="print:hidden bg-[#0A0A0A] text-[#F4F3ED] sticky top-0 z-30">
        <div className="max-w-[860px] mx-auto px-5 py-3 flex items-center justify-between">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase">
            Community Handbook · Save as PDF →
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 bg-[#D9381E] text-white px-3 py-2 font-mono-acc text-[10px] tracking-[0.25em] uppercase hover:bg-[#0A0A0A] border border-[#D9381E]"
            >
              <Printer size={14} weight="bold" /> Print / Save PDF
            </button>
            <button
              onClick={() => window.close()}
              className="text-[#A1A1AA] hover:text-white p-1.5"
              aria-label="Close"
            >
              <IconX size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* A4-ish handbook */}
      <main
        className="max-w-[860px] mx-auto px-8 sm:px-12 py-12 print:py-8 print:px-10 print:max-w-none"
        style={{ fontFamily: "Satoshi, ui-sans-serif, system-ui, sans-serif" }}
      >
        {/* Cover */}
        <header className="border-b-4 border-[#0A0A0A] pb-8 mb-8">
          <div className="flex items-center justify-between gap-4 mb-8">
            <Logo className="h-16 w-auto" />
            <div className="text-right">
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]">
                COMMUNITY HANDBOOK
              </div>
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E]">
                v1 · ₹999 cohort
              </div>
            </div>
          </div>
          <h1
            className="font-black text-5xl sm:text-6xl tracking-tighter leading-[0.9]"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Welcome to the
            <br />
            <span className="text-[#D9381E]">Great Eagle</span> live room.
          </h1>
          <p className="mt-6 text-[#4A4A4A] max-w-xl leading-relaxed">
            A short, no-fluff guide to using this community properly. Read it
            once before your first live session — it will save you weeks.
          </p>
        </header>

        {/* Section 1 — How the live works */}
        <Section number="01" title="How the live session works">
          <ol className="space-y-4">
            {LIVE_FLOW.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-mono-acc text-[10px] tracking-[0.25em] text-[#D9381E] pt-1 w-24 shrink-0 uppercase">
                  {s.step}
                </span>
                <p className="text-sm text-[#0A0A0A] leading-relaxed flex-1">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </Section>

        {/* Section 2 — Market Profile basics */}
        <Section number="02" title="Market Profile · the basics">
          <p className="text-sm text-[#4A4A4A] mb-5 leading-relaxed">
            We will use these terms every single day in the live room. Read
            them. Re-read them. Don't ask "what is POC sir?" mid-session.
          </p>
          <dl className="space-y-4">
            {TERMS.map((t, i) => (
              <div key={i} className="border-l-4 border-[#D9381E] pl-4 py-1">
                <dt className="font-bold text-[#0A0A0A]">{t.term}</dt>
                <dd className="text-sm text-[#4A4A4A] mt-1 leading-relaxed">
                  {t.desc}
                </dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* Section 3 — TPO schedule */}
        <Section number="03" title="TPO period schedule · IST">
          <p className="text-sm text-[#4A4A4A] mb-5 leading-relaxed">
            Each market day is broken into 13 thirty-minute brackets. Each
            bracket gets a letter. When we say "B-period high", we mean the
            high made between 09:45 and 10:15.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {TPO_PERIODS.map(([letter, range]) => (
              <div
                key={letter}
                className="border border-[#0A0A0A] px-3 py-2 flex items-center gap-3"
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 bg-[#0A0A0A] text-white font-mono-acc font-bold text-sm"
                >
                  {letter}
                </span>
                <span className="font-mono-acc text-xs">{range}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#4A4A4A] mt-4 leading-relaxed italic">
            Note · M-period is short (15 mins) — it's the closing auction. The
            entire day = 13 TPOs.
          </p>
        </Section>

        {/* Section 4 — Group rules */}
        <Section number="04" title="Group etiquette · read twice">
          <ul className="space-y-2.5">
            {RULES.map((r, i) => (
              <li
                key={i}
                className="flex gap-3 text-sm text-[#0A0A0A] leading-relaxed"
              >
                <span className="font-mono-acc text-[#D9381E] shrink-0 mt-0.5">
                  →
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Section 5 — Understanding Daily Levels */}
        <Section number="05" title="Understanding daily levels">
          <p className="text-sm text-[#4A4A4A] mb-4 leading-relaxed">
            Every trading day, before market open, a levels chart like the one
            below is posted in the group:
          </p>
          <div className="border border-[#0A0A0A] p-5 bg-[#FAF8F3] overflow-x-auto">
            <div className="flex items-center gap-4 mb-3">
              <div className="font-display font-black text-base">Level Trades</div>
              <div className="font-mono-acc text-xs text-[#4A4A4A]">15 May 2026</div>
            </div>
            <table className="w-full text-xs font-mono-acc min-w-[420px]">
              <thead>
                <tr className="border-b border-[#0A0A0A]">
                  <th className="text-left py-1.5 font-bold">LEVEL</th>
                  <th className="text-right py-1.5 font-bold">NIFTY</th>
                  <th className="text-right py-1.5 font-bold">BNIFTY</th>
                  <th className="text-right py-1.5 font-bold">MIDCAP</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["R3", "23,970", "55,230", "14,500"],
                  ["R2", "23,900", "55,000", "14,448"],
                  ["R1", "23,857", "54,879", "14,400"],
                  ["BULLISH", "23,690", "54,600", "14,350", "#C9E4C9"],
                  ["POC", "23,690", "54,349", "14,152", "#F08CD1"],
                  ["BEARISH", "23,540", "53,784", "14,080", "#D9938F"],
                  ["S1", "23,490", "53,585", "14,040"],
                  ["S2", "23,440", "53,400", "13,990"],
                  ["S3", "23,385", "53,185", "13,935"],
                ].map(([k, n, b, m, bg]) => (
                  <tr
                    key={k}
                    className="border-b border-[#D4D0C5]"
                    style={bg ? { backgroundColor: bg } : {}}
                  >
                    <td className="py-1.5 font-bold">{k}</td>
                    <td className="py-1.5 text-right">{n}</td>
                    <td className="py-1.5 text-right">{b}</td>
                    <td className="py-1.5 text-right">{m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            {LEVELS_GLOSSARY.map(([k, v]) => (
              <div key={k} className="border-l-2 border-[#3B5BDB] pl-3">
                <dt className="font-bold text-sm text-[#0A0A0A]">{k}</dt>
                <dd className="text-xs text-[#4A4A4A] mt-0.5">{v}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* Section 6 — GoCharting */}
        <Section number="06" title="GoCharting · 1 month free">
          <p className="text-sm text-[#0A0A0A] leading-relaxed">
            Sign up at <strong>gocharting.com</strong> using a{" "}
            <strong>brand-new email</strong> (not used before on GoCharting) and
            you get one month of Premium free. Templates for the Zoom sessions
            will be shared inside the group on day one.
          </p>
        </Section>

        {/* Section 7 — Risk disclaimer */}
        <Section number="07" title="Risk disclaimer">
          <ul className="space-y-2.5">
            {RISK.map((r, i) => (
              <li
                key={i}
                className="flex gap-3 text-sm text-[#0A0A0A] leading-relaxed"
              >
                <span className="font-mono-acc text-[#D9381E] shrink-0 mt-0.5">
                  ⚠
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Section 8 — Contact */}
        <Section number="08" title="If you have trouble">
          <ul className="space-y-2 text-sm leading-relaxed">
            <li>
              <span className="text-[#4A4A4A]">WhatsApp · </span>
              <strong>+91 81293 87138</strong>
            </li>
            <li>
              <span className="text-[#4A4A4A]">WhatsApp · </span>
              <strong>+91 81293 87137</strong>
            </li>
            <li>
              <span className="text-[#4A4A4A]">Email · </span>
              <strong>office.greateagle@gmail.com</strong>
            </li>
            <li>
              <span className="text-[#4A4A4A]">Site · </span>
              <strong>greateagle.co.in</strong>
            </li>
          </ul>
        </Section>

        <footer className="mt-10 pt-6 border-t border-[#D4D0C5] text-xs text-[#4A4A4A] leading-relaxed">
          <strong>Disclaimer · </strong>
          Great Eagle Tech is an education and mentorship community. We are not
          SEBI-registered investment advisers. All trading involves risk — you
          alone are responsible for your decisions. Past performance and
          testimonials do not guarantee future results.
        </footer>
      </main>

      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="mb-10 break-inside-avoid">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono-acc text-[11px] tracking-[0.3em] uppercase text-[#D9381E]">
          {number}
        </span>
        <h2
          className="font-black text-2xl sm:text-3xl tracking-tighter"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          {title}
        </h2>
      </div>
      <div>{children}</div>
    </section>
  );
}
